import { ConnectorValue, expandConnectorViewPayload } from 'lib/actions/apps/app-connector'
import { ConnectorCatalogValue, ConnectorInstallationValue, expandConnectorCatalogView } from 'lib/actions/connector_catalog/index'
import { expandValue } from 'lib/rpcts'
import { App } from 'pbts/c1/api/app/v1/app'
import {
    C1AppConfig, Connector, ConnectorRef, ConnectorServiceGetResponse, ConnectorSyncLifecycle, ConnectorSyncLifecycleRef, EnvConfig,
    FileConfig,
} from 'pbts/c1/api/app/v1/connector'
import { AppConnectorAccountCreationSchemaServiceClient, ConnectorServiceClient } from 'pbts/c1/api/app/v1/connector.client'
import { File } from 'pbts/c1/api/filestore/v1/filestore'
import { ConnectorInstallation, ConnectorInstallationView } from 'pbts/c1/api/integration/connector/v1/catalog'
import { ConnectorCatalogServiceClient } from 'pbts/c1/api/integration/connector/v1/catalog.client'
import { Any } from 'pbts/google/protobuf/any'
import { createMutationApi } from 'redux_toolkit/lib/createMutationApi'
import { createPagedListApi } from 'redux_toolkit/lib/createPagedListApi'
import { createUnaryApi, createUnaryApiNoCache } from 'redux_toolkit/lib/createUnaryApi'

const expandConnectorInstallationView = (
    msg: ConnectorInstallationView | undefined,
    values: Any[],
): ConnectorInstallationValue => {
    const resp: ConnectorInstallationValue = {
        ...ConnectorInstallation.create(msg?.connectorInstallation ?? {}),
        id: msg?.connectorInstallation?.connectorId || '',
        connector: expandValue<Connector>(msg?.connectorPath, Connector, values),
        app: expandValue<App>(msg?.appPath, App, values),
        connectorSyncLifecycle: expandValue<ConnectorSyncLifecycle>(msg?.syncLifecyclePath, ConnectorSyncLifecycle, values),
        hasAnyCompletedIncrementalSync: msg?.hasAnyCompletedIncrementalSync ?? false,
        lambdaDeploymentVersion: msg?.lambdaDeploymentVersion,
        lambdaDeploymentChannel: msg?.lambdaDeploymentChannel,
    }

    resp.configType = resp.connector?.config?.typeUrl

    if (resp.connector && resp.connector.config) {
        switch (Any.typeUrlToName(resp.connector.config.typeUrl)) {
            case FileConfig.typeName:
                resp.connector.config = Any.unpack<FileConfig>(resp.connector.config, FileConfig) as unknown as Any

                // We need to delete the binary file data here because Redux will complain if we put binary data in the
                // Redux state: "A non-serializable value was detected in the state"
                // eslint-disable-next-line no-case-declarations
                const config = resp?.connector.config as unknown as FileConfig
                delete (config?.file as Partial<File>).file

                break
            case EnvConfig.typeName:
                resp.connector.config = Any.unpack<EnvConfig>(resp.connector.config, EnvConfig) as unknown as Any
                break
            case C1AppConfig.typeName:
                resp.connector.config = Any.unpack<C1AppConfig>(resp.connector.config, C1AppConfig) as unknown as Any
            // no default
        }
    }
    return resp
}

export const listInstallationsConnectorCatalogApi = createPagedListApi(
    ConnectorCatalogServiceClient,
    'listInstallations',
    (resp): Array<ConnectorInstallationValue> => {
        return resp.list.map((v) => expandConnectorInstallationView(v, resp.expanded))
    },
)

export const searchInstallationsApi = createPagedListApi(
    ConnectorCatalogServiceClient,
    'searchInstallations',
    (resp): Array<ConnectorInstallationValue> => {
        return resp.list.map((v) => expandConnectorInstallationView(v, resp.expanded))
    },
    ConnectorRef.typeName,
)

export const getInstallationApi = createUnaryApi(
    ConnectorCatalogServiceClient,
    'getInstallation',
    (resp): ConnectorInstallationValue => {
        return expandConnectorInstallationView(resp.connectorInstallation, resp.expanded)
    },
    ConnectorRef.typeName,
)

export const searchConnectorCatalogApi = createPagedListApi(
    ConnectorCatalogServiceClient,
    'search',
    (resp): Array<ConnectorCatalogValue> => {
        return resp.list.map((v) => expandConnectorCatalogView(v, resp.expanded))
    },
)

export const getConnectorConfigurationSchemaApi = createUnaryApi(
    ConnectorCatalogServiceClient,
    'configurationSchema',
)

export const getConnectorCatalogApi = createUnaryApi(
    ConnectorCatalogServiceClient,
    'get',
    (resp): ConnectorCatalogValue => {
        return expandConnectorCatalogView(resp.catalogView, [])
    },
)

const transformGetConnectorsResponse = (resp: ConnectorServiceGetResponse): ConnectorValue => {
    return expandConnectorViewPayload(resp.connectorView, resp.expanded)
}

export const getConnectorApi = createUnaryApi(
    ConnectorServiceClient,
    'get',
    transformGetConnectorsResponse,
)

export const getConnectorApiNoCache = createUnaryApiNoCache(
    ConnectorServiceClient,
    'get',
    transformGetConnectorsResponse,
)

export const createConnectorApi = createMutationApi(
    ConnectorServiceClient,
    'create',
)

export const createDelegatedConnectorApi = createMutationApi(
    ConnectorServiceClient,
    'createDelegated',
)

export const deleteConnectorApi = createMutationApi(
    ConnectorServiceClient,
    'delete',
)

export const updateConnectorApi = createMutationApi(
    ConnectorServiceClient,
    'update',
)
export const updateConnectorSyncResourceTypesConfigApi = createMutationApi(
    ConnectorServiceClient,
    'updateConnectorSyncResourceTypesConfig',
)

export const updateConnectorAllowIgnoreListApi = createMutationApi(
    ConnectorServiceClient,
    'updateAllowIgnoreList',
)

export const updateAdvancedConfigApi = createMutationApi(
    ConnectorServiceClient,
    'updateAdvancedConfig',
)

export const updateDelegatedConnectorApi = createMutationApi(
    ConnectorServiceClient,
    'updateDelegated',
    (resp) => resp.connectorView || {},
    ConnectorRef.typeName,
)

export const listConnectorLogsApi = createPagedListApi(
    ConnectorServiceClient,
    'logs',
)

export const forceSyncConnectorApi = createMutationApi(
    ConnectorServiceClient,
    'forceSync',
)

export const resumeSyncConnectorApi = createMutationApi(
    ConnectorServiceClient,
    'resumeSync',
)

export const confirmSyncValidConnectorApi = createMutationApi(
    ConnectorServiceClient,
    'confirmSyncValid',
)

export const pauseSyncConnectorApi = createMutationApi(
    ConnectorServiceClient,
    'pauseSync',
)

export const getConnectorSyncLifecycle = createUnaryApi(
    ConnectorServiceClient,
    'getConnectorSyncLifecycle',
    undefined,
    ConnectorSyncLifecycleRef.typeName,
)

export const listCompletedConnectorSyncLifecycleApi = createPagedListApi(
    ConnectorServiceClient,
    'listCompletedConnectorSyncLifecycle',
    (resp) => resp.list,
    ConnectorSyncLifecycleRef.typeName,
)

export const listConnectorSyncLifecycleApi = createPagedListApi(
    ConnectorServiceClient,
    'listConnectorSyncLifecycle',
    (resp) => resp.list,
    ConnectorSyncLifecycleRef.typeName,
)

export const getConnectorSchemaApi = createUnaryApi(
    AppConnectorAccountCreationSchemaServiceClient,
    'get',
    undefined,
)

export const getConnectorExternalSourceConfigApi = createUnaryApi(
    ConnectorServiceClient,
    'getConnectorExternalSourceConfig',
    undefined,
)

export const setConnectorExternalSourceConfigApi = createMutationApi(
    ConnectorServiceClient,
    'setConnectorExternalSourceConfig',
)

export const getConnectorSyncDownloadURLApi = createUnaryApiNoCache(
    ConnectorServiceClient,
    'getConnectorSyncDownloadURL',
    undefined,
)

export const validateHTTPConnectorConfigApi = createUnaryApi(
    ConnectorServiceClient,
    'validateHTTPConnectorConfig',
)

export const updateConnectorScheduleApi = createMutationApi(
    ConnectorServiceClient,
    'updateConnectorSchedule',
)
